/**
 * TableRegistry
 *
 * Thin wrapper around Map<string, TableHandle> exposed as adapter.tables.
 * Provides sugar methods beyond the raw Map API.
 */
export class TableRegistry {
    constructor() {
        this._map = new Map();
    }

    /**
     * Register a TableHandle (called internally by addTable).
     * @param {string}      id
     * @param {TableHandle} handle
     * @internal
     */
    _register(id, handle) {
        this._map.set(id, handle);
    }

    // ── Public API ────────────────────────────────────────────────────────────

    /**
     * Fetch a TableHandle by its ID.
     * @param {string} id
     * @returns {TableHandle|undefined}
     */
    get(id) {
        return this._map.get(id);
    }

    /**
     * Check whether a table with the given ID has been registered.
     * @param {string} id
     * @returns {boolean}
     */
    has(id) {
        return this._map.has(id);
    }

    /**
     * All registered table IDs in insertion order.
     * @returns {string[]}
     */
    ids() {
        return [...this._map.keys()];
    }

    /**
     * All TableHandle instances in insertion order.
     * @returns {TableHandle[]}
     */
    all() {
        return [...this._map.values()];
    }

    /**
     * [id, handle] pairs — mirrors Map.entries().
     * @returns {[string, TableHandle][]}
     */
    entries() {
        return [...this._map.entries()];
    }

    /**
     * Iterate over all handles.
     * Callback receives (handle, id) — handle first for ergonomics.
     * @param {(handle: TableHandle, id: string) => void} fn
     */
    forEach(fn) {
        this._map.forEach((handle, id) => fn(handle, id));
    }

    /** Number of registered tables. */
    get size() {
        return this._map.size;
    }

    /** Make the registry itself iterable: `for (const handle of adapter.tables)`. */
    [Symbol.iterator]() {
        return this._map.values();
    }
}
